// How long a pointer that left the trigger has to reach the panel, across the gap between them, before
// the panel closes: without it the panel hides on the way in, and can't be hovered (WCAG 1.4.13).
const LEAVE_GRACE_MS = 150;

// How far a panel kept on screen stays inside the window's sides: x-anchor's own shift padding, so all four edges match.
const EDGE_PX = 5;

// What a panel's controls are, for the keyboard (tt_focusables()): Why it ranks' checks (D153) are buttons, and the
// funnel's ⓘ holds a disclosure (D170), whose summary a browser focuses as it does a button.
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

// Where a follower sits from the pointer (tt_place()), the Flow's tooltip's (D205): right of it and below, or on its
// other side wherever the window has no room.
const FOLLOW_ASIDE_PX = 16;
const FOLLOW_BELOW_PX = 20;
const FOLLOW_ABOVE_PX = 16;

// The window event a follower's hover sends, so every other follower closes at once (tt_another()).
const FOLLOW_EVENT = "insights-tooltip-follow";

/**
 * The Alpine half of <x-insights.tooltip> (D67), on components/site-analytics/card-info.blade.php's
 * pattern, and WCAG 1.4.13's three rules:
 * - hover or keyboard focus opens the panel, and a click pins it until the next click outside or Tab away;
 * - Hoverable: the pointer may cross to the panel and rest on it;
 * - Dismissible: Escape closes it without moving the pointer or focus, and it stays closed until the
 *   pointer leaves or focus moves on;
 * - Persistent: nothing closes it on a timer.
 *
 * A panel with controls in it (Why it ranks' checks, D153) is a disclosure a keyboard can enter: once the trigger
 * is pinned (Enter, Space or a click), Tab moves into the panel, focus inside it keeps it open, Tab past its last
 * control or Shift+Tab before its first returns to the trigger, and Escape inside it closes it and returns focus
 * there. Hover and focus alone never pull focus in: a keyboard user tabbing down a column of numbers passes each
 * panel by. The panel lives in <body> (x-teleport), so its focus events reach here through its own handlers.
 *
 * A follower (`follow`, the component's prop; D205) is a panel of words a mouse or a pen opens: it sits beside the
 * pointer and moves with it (tt_place()), takes no pointer events, and closes the moment the pointer leaves, or as
 * another follower opens. A finger's tap is its press, and only keyboard focus opens it by focus: a press focuses its
 * trigger too, and that focus would hold the panel open once the pointer moved on. Pinned by a press, or opened by
 * the keyboard, it sits against its trigger as any panel does.
 */
export default function InsightsTooltip({ follow = false } = {}) {
    return {
        tt_hover: false,
        tt_focus: false,
        tt_pinned: false,
        tt_dismissed: false,
        tt_leaveTimer: null,
        tt_follow: follow,
        // The pointer in the window while a follower's trigger has it, else null.
        tt_at: null,

        tt_enter(event = null) {
            clearTimeout(this.tt_leaveTimer);

            if (this.tt_follow && event !== null) {
                if (event.pointerType === "touch") {
                    return;
                }

                this.tt_at = { x: event.clientX, y: event.clientY };
                window.dispatchEvent(new CustomEvent(FOLLOW_EVENT, { detail: this.$root }));
            }

            this.tt_hover = true;
        },

        tt_move(event) {
            if (event.pointerType !== "touch") {
                this.tt_at = { x: event.clientX, y: event.clientY };
            }
        },

        tt_leave(event = null) {
            clearTimeout(this.tt_leaveTimer);

            if (this.tt_follow && event !== null) {
                if (event.pointerType === "touch") {
                    return;
                }

                // Convention exception - WCAG 1.4.13 Hoverable (accessibility.md §9, Tooltip):
                // it moves with the pointer, so nothing crosses to it; pinned by a press, it stays as the pointer leaves.
                this.tt_hover = false;
                this.tt_at = null;
                this.tt_dismissed = false;

                return;
            }

            this.tt_leaveTimer = setTimeout(() => {
                this.tt_hover = false;
                this.tt_dismissed = false;
            }, LEAVE_GRACE_MS);
        },

        // Another follower opened: this one closes, a press's pin too, so two never show at once and none is left
        // standing over the next thing pointed at.
        tt_another(event) {
            if (event.detail === this.$root) {
                return;
            }

            this.tt_hover = false;
            this.tt_at = null;
            this.tt_pinned = false;
        },

        tt_focusIn(event = null) {
            if (this.tt_follow && event?.target?.matches?.(":focus-visible") === false) {
                return;
            }

            this.tt_focus = true;
            this.tt_dismissed = false;
        },

        // Focus leaving the trigger closes the panel, unless it is going into the panel's own controls (tt_tabIn()).
        tt_focusOut(event = null) {
            if (event?.relatedTarget && this.$refs.panel?.contains(event.relatedTarget)) {
                return;
            }

            this.tt_focus = false;
            this.tt_pinned = false;
            this.tt_dismissed = false;
        },

        // The panel's controls a keyboard can reach, in their order; none while it is hidden.
        tt_focusables() {
            const panel = this.$refs.panel;

            return panel ? Array.from(panel.querySelectorAll(FOCUSABLE)).filter((control) => control.offsetParent !== null) : [];
        },

        // Tab on a pinned trigger goes into the panel, to its first control; Shift+Tab, an unpinned panel or one with
        // no controls leave the key to the browser.
        tt_tabIn(event) {
            const first = this.tt_pinned && !event.shiftKey ? this.tt_focusables()[0] : undefined;

            if (!first) {
                return;
            }

            event.preventDefault();
            first.focus();
        },

        // Focus inside the panel keeps it open, as focus on the trigger does.
        tt_panelFocusIn() {
            this.tt_focus = true;
            this.tt_dismissed = false;
        },

        // Focus leaving the panel for anywhere but its trigger or its own controls closes it, as Tab away does.
        tt_panelFocusOut(event) {
            const to = event?.relatedTarget;

            if (to && (this.$refs.panel?.contains(to) || this.$refs.trigger?.contains(to))) {
                return;
            }

            this.tt_focus = false;
            this.tt_pinned = false;
            this.tt_dismissed = false;
        },

        // Tab past the panel's last control, or Shift+Tab before its first, returns focus to the trigger and unpins
        // the panel: the panel sits at the end of the page, so the page's order continues from the trigger.
        tt_panelTab(event) {
            const controls = this.tt_focusables();
            const at = controls.indexOf(document.activeElement);

            if (controls.length === 0 || (event.shiftKey ? at !== 0 : at !== controls.length - 1)) {
                return;
            }

            event.preventDefault();
            this.$refs.trigger?.focus();
            this.tt_pinned = false;
        },

        // Escape inside the panel: focus back on the trigger first, then dismissed, so the trigger's focus does not
        // open the panel again (WCAG 1.4.13's Dismissible, from inside).
        tt_escapeInside() {
            this.$refs.trigger?.focus();
            this.tt_dismiss();
        },

        tt_dismiss() {
            this.tt_pinned = false;
            this.tt_dismissed = true;
        },

        // Safari does not focus a clicked button, so the press focuses it: otherwise Escape and Tab away
        // never reach a panel a click pinned open.
        tt_toggle(trigger) {
            this.tt_pinned = !this.tt_pinned;
            this.tt_dismissed = false;
            trigger.focus();
        },

        /**
         * Where a panel kept on screen sits (the component's onScreen): where x-anchor puts it, fixed to the window,
         * held inside the window's sides. x-anchor already holds it inside the top and bottom (its shift, along the
         * side it opens on, with the panel never taller than the window); across, nothing does, so a panel wider than
         * the room beside its trigger, as on a phone, would leave the window.
         */
        // The panel fixed to the window: where x-anchor put it, held EDGE_PX inside the window's sides, and moved up as
        // far as it needs to end inside the window's bottom, or, taller than the window, from its top with its own
        // scroll (its max height). Without the move, the funnel's ⓘ a third of the way down a 900px window, or a low
        // row's Why it ranks, ran off the bottom with its last lines, the guide link among them, reachable by Tab but
        // never on screen (2.4.11).
        tt_onScreen(anchor, panel) {
            const roomX = typeof window === "undefined" ? Infinity : window.innerWidth;
            const roomY = typeof window === "undefined" ? Infinity : (window.innerHeight ?? Infinity);
            const width = panel?.offsetWidth ?? 0;
            const height = panel?.offsetHeight ?? 0;
            const style = {
                position: "fixed",
                top: `${Math.max(EDGE_PX, Math.min(anchor.y, roomY - height - EDGE_PX))}px`,
                left: `${Math.max(EDGE_PX, Math.min(anchor.x, roomX - width - EDGE_PX))}px`,
            };

            if (roomY !== Infinity) {
                style.maxHeight = `${roomY - 2 * EDGE_PX}px`;
            }

            return style;
        },

        // Whether a follower sits at the pointer: hovered, and neither pinned by a press nor opened by the keyboard.
        tt_following() {
            return this.tt_follow && this.tt_hover && this.tt_at !== null && !this.tt_pinned && !this.tt_focus;
        },

        // A follower's place (the component's follow): beside the pointer, right of it and below, on its other side
        // wherever the window has no room, and never past the window's top or left; against its trigger and kept on
        // screen (tt_onScreen()) while it is not following.
        tt_place(anchor, panel) {
            if (!this.tt_following()) {
                return this.tt_onScreen(anchor, panel);
            }

            const roomX = typeof window === "undefined" ? Infinity : window.innerWidth;
            const roomY = typeof window === "undefined" ? Infinity : (window.innerHeight ?? Infinity);
            const width = panel?.offsetWidth ?? 0;
            const height = panel?.offsetHeight ?? 0;
            const { x, y } = this.tt_at;
            const left = x + FOLLOW_ASIDE_PX + width + EDGE_PX > roomX ? x - FOLLOW_ASIDE_PX - width : x + FOLLOW_ASIDE_PX;
            const top = y + FOLLOW_BELOW_PX + height + EDGE_PX > roomY ? y - FOLLOW_ABOVE_PX - height : y + FOLLOW_BELOW_PX;

            return {
                position: "fixed",
                top: `${Math.max(EDGE_PX, top)}px`,
                left: `${Math.max(EDGE_PX, left)}px`,
            };
        },

        // The panel lives in <body> (x-teleport), so a click inside it is not a click away.
        tt_clickAway(event) {
            if (!this.$refs.panel?.contains(event.target)) {
                this.tt_pinned = false;
            }
        },
    };
}
